/**
 * VaultFS over a plain HTTP API.
 *
 * Backs the dev server (`packages/app/plugins/vault-dev-server.ts`). The
 * GitHub adapter in M3 implements the same interface, so nothing above this
 * line knows or cares which one is mounted.
 */

import {
  VaultConflictError,
  VaultNotFoundError,
  type VaultEntry,
  type VaultFile,
  type VaultFS,
} from '@hud/core';

export interface HttpVaultOptions {
  /** Base URL of the vault API, e.g. `/__vault`. No trailing slash. */
  baseUrl: string;
  fetchImpl?: typeof fetch;
  headers?: Record<string, string>;
}

export class HttpVaultFS implements VaultFS {
  readonly #baseUrl: string;
  readonly #fetch: typeof fetch;
  readonly #headers: Record<string, string>;

  constructor(options: HttpVaultOptions) {
    this.#baseUrl = options.baseUrl.replace(/\/$/, '');
    this.#fetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.#headers = options.headers ?? {};
  }

  async list(): Promise<VaultEntry[]> {
    const res = await this.#fetch(`${this.#baseUrl}/tree`, { headers: this.#headers });
    if (!res.ok) throw new Error(`Vault tree failed: ${res.status} ${await safeText(res)}`);
    const body = (await res.json()) as { entries?: VaultEntry[] };
    return body.entries ?? [];
  }

  async read(path: string): Promise<VaultFile> {
    const res = await this.#fetch(this.#fileUrl(path), { headers: this.#headers });
    if (res.status === 404) throw new VaultNotFoundError(path);
    if (!res.ok) throw new Error(`Vault read failed for ${path}: ${res.status} ${await safeText(res)}`);
    return (await res.json()) as VaultFile;
  }

  async write(path: string, content: string, baseSha?: string): Promise<{ sha: string }> {
    const res = await this.#fetch(this.#fileUrl(path), {
      method: 'PUT',
      headers: { ...this.#headers, 'content-type': 'application/json' },
      body: JSON.stringify({ content, baseSha }),
    });

    if (res.status === 409) {
      const body = (await res.json().catch(() => ({}))) as { actualSha?: string };
      throw new VaultConflictError(path, baseSha, body.actualSha);
    }
    if (!res.ok) throw new Error(`Vault write failed for ${path}: ${res.status} ${await safeText(res)}`);
    return (await res.json()) as { sha: string };
  }

  async remove(path: string, baseSha?: string): Promise<void> {
    const url = new URL(this.#fileUrl(path), globalThis.location?.origin ?? 'http://localhost');
    if (baseSha) url.searchParams.set('baseSha', baseSha);

    const res = await this.#fetch(url.pathname + url.search, {
      method: 'DELETE',
      headers: this.#headers,
    });

    if (res.status === 404) throw new VaultNotFoundError(path);
    if (res.status === 409) throw new VaultConflictError(path, baseSha, undefined);
    if (!res.ok) throw new Error(`Vault delete failed for ${path}: ${res.status} ${await safeText(res)}`);
  }

  #fileUrl(path: string): string {
    return `${this.#baseUrl}/file?path=${encodeURIComponent(path)}`;
  }
}

async function safeText(res: Response): Promise<string> {
  try {
    return (await res.text()).slice(0, 200);
  } catch {
    return '';
  }
}
